/**
 * Sentry sampling policy: one table, keyed by deploy environment and service.
 *
 * Dependency-free and browser-safe, because it is imported by the `instrument.ts`
 * of all four Node services *and* by the Vite web bundle. It must not pull
 * anything in behind it, and must not touch `node:*` or assume `process` exists.
 *
 * ## Why the policy lives in code and not in environment variables
 *
 * The obvious design is one environment variable per knob:
 * `SENTRY_TRACES_SAMPLE_RATE_API`, `..._WORKER`, `..._CRON`, `..._MCP`, and the
 * same again for profiles. Ten variables, each of which has to be added to
 * `app-env-manifest.json`, generated into the workflow env blocks, set as a
 * GitHub variable per environment, and stored in App Configuration.
 *
 * That is the wrong trade. Sampling policy is a *reviewable engineering
 * decision* — it decides what we can debug and what we pay — and it belongs in
 * a diff, not in a console where a click leaves no trace. Ten variables also
 * fail open in the worst way: the one you forget to set silently reverts to a
 * default nobody wrote down, which is precisely how
 * `SENTRY_TRACES_SAMPLE_RATE` came to be read by the worker, documented in
 * `.env.example`, absent from the manifest, and dead for its whole life.
 *
 * So the table below IS the policy. It is version-controlled, diffable, and
 * applied identically everywhere. No variable needs to be set for a correct
 * deploy — which also means no variable can be forgotten.
 *
 * ## Which environment am I in?
 *
 * Not `NODE_ENV`: the runtime images hardcode `NODE_ENV=production`, so QA,
 * preview and production are indistinguishable by it. `SENTRY_ENVIRONMENT`
 * (`VITE_SENTRY_ENVIRONMENT` in the browser) is already set per environment and
 * already in the manifest, so it is the discriminator — no new variable.
 *
 * An unrecognised value resolves to the PRODUCTION budget, not the permissive
 * one. A typo should cost visibility, never money.
 *
 * ## The one escape hatch
 *
 * `SENTRY_SAMPLING` overrides any cell, sparsely, without a deploy:
 *
 *   SENTRY_SAMPLING="traces.cron=1,traces.api=0.5,profiles.worker=0.05"
 *   SENTRY_SAMPLING="traces=0"            # every service, this kind
 *
 * A service-specific entry beats a kind-wide one. Anything unparseable — bad
 * kind, bad service, rate outside 0..1 — is IGNORED and announced, never
 * clamped and never thrown: an `instrument.ts` that throws takes the process
 * with it, and clamping a typed `100` to `1` would quietly buy full sampling.
 *
 * It exists for the 3am case ("turn cron tracing up while I look at this") and
 * for temporary profiling. A value that survives a week belongs in the table.
 */

/**
 * Services with their own sampling budget.
 *
 * `cron` is the ACA scheduled jobs. They run the worker image and share its DSN
 * and Sentry project; `JOB_ID` being set is the only thing that tells them
 * apart, which is what lets them carry a different budget without a second
 * project.
 */
export type SentryService = 'api' | 'worker' | 'cron' | 'mcp' | 'web';

export type SentrySampleKind = 'traces' | 'profiles' | 'replays' | 'replaysOnError';

/** Normalised deploy environments. Aliases are folded in `normalizeEnvironment`. */
export type SentryDeployEnvironment = 'prod' | 'qa' | 'preview' | 'dev';

export interface SentrySamplingPolicy {
  /** Fraction of root transactions traced. Drives the spans bill. */
  traces: number;
  /**
   * Fraction of *already-traced* transactions profiled — multiplicative with
   * `traces`, and billed in CPU hours rather than by count.
   */
  profiles: number;
  /** Browser session replay, recorded routinely. Web only. */
  replays: number;
  /** Browser session replay, recorded because an error occurred. Web only. */
  replaysOnError: number;
}

const NONE = { profiles: 0, replays: 0, replaysOnError: 0 } as const;

/**
 * The policy. Every number here is a decision; the comments say which.
 *
 * Two rules run through the whole table:
 *
 * - **Errors are never sampled.** `sampleRate` stays at its default of 1.0 in
 *   every service. Errors are the cheapest line Sentry bills and the only one
 *   that cannot be reconstructed after the fact.
 * - **Profiling is opt-in everywhere, including locally.** The integration
 *   carries memory and CPU cost even at a zero rate (it contributed to the
 *   worker's resident-memory pressure), and it is billed per hour rather than
 *   per event. Turn it on through `SENTRY_SAMPLING` while investigating a
 *   specific regression, then turn it off.
 */
const POLICY: Record<SentryDeployEnvironment, Record<SentryService, SentrySamplingPolicy>> = {
  prod: {
    // Real user traffic. 10% is enough to characterise latency and catch the
    // shape of a regression; the errors that matter arrive at 100% regardless.
    api: { traces: 0.1, ...NONE },
    // The 44 BullMQ processors open no root span, so once the parentless Redis
    // and Prisma transactions are dropped there is almost nothing left here to
    // sample. Kept low and honest rather than raised: the fix is to wrap job
    // processing in a root span, not to buy more orphans.
    worker: { traces: 0.2, ...NONE },
    // A few hundred executions a day across 18 jobs, each now wrapped in one
    // root span by `dispatchCronJob` — so this buys 18 real traces a day with
    // their queries underneath, not 18 piles of orphans. The cheapest thing in
    // the system to trace fully, and the one that ran dead for five weeks
    // without anybody noticing.
    cron: { traces: 1, ...NONE },
    // Low volume, and agent traffic is usually the thing being debugged.
    mcp: { traces: 0.2, ...NONE },
    // Browser spans are noisy and mostly describe the network, which the API
    // side already traces. Session replay is OFF as routine and ON for errors:
    // that is the only shape where the recording is worth its privacy cost.
    web: { traces: 0.05, profiles: 0, replays: 0, replaysOnError: 1 },
  },
  qa: {
    // QA is not free — it bills against the same organisation quota — but its
    // volume is test traffic, and a QA trace usually exists because somebody is
    // looking for it right now.
    api: { traces: 0.5, ...NONE },
    worker: { traces: 0.5, ...NONE },
    cron: { traces: 1, ...NONE },
    mcp: { traces: 1, ...NONE },
    web: { traces: 0.2, profiles: 0, replays: 0, replaysOnError: 1 },
  },
  preview: {
    // Ephemeral per-PR environments: low traffic, short-lived, and their whole
    // purpose is that somebody is watching them. Same budget as QA so the two
    // can diverge later without rewriting the shape of this table.
    api: { traces: 0.5, ...NONE },
    worker: { traces: 0.5, ...NONE },
    cron: { traces: 1, ...NONE },
    mcp: { traces: 1, ...NONE },
    web: { traces: 0.2, profiles: 0, replays: 0, replaysOnError: 1 },
  },
  dev: {
    // A laptop. Sentry is normally disabled outright here (no DSN), so these
    // apply only when a developer has deliberately pointed one at a project.
    api: { traces: 1, ...NONE },
    worker: { traces: 1, ...NONE },
    cron: { traces: 1, ...NONE },
    mcp: { traces: 1, ...NONE },
    web: { traces: 1, profiles: 0, replays: 0, replaysOnError: 1 },
  },
};

const ENVIRONMENT_ALIASES: Record<string, SentryDeployEnvironment> = {
  prod: 'prod',
  production: 'prod',
  prd: 'prod',
  qa: 'qa',
  staging: 'qa',
  stage: 'qa',
  stg: 'qa',
  preview: 'preview',
  pr: 'preview',
  ephemeral: 'preview',
  dev: 'dev',
  development: 'dev',
  local: 'dev',
  test: 'dev',
};

const KINDS: readonly SentrySampleKind[] = ['traces', 'profiles', 'replays', 'replaysOnError'];
const SERVICES: readonly SentryService[] = ['api', 'worker', 'cron', 'mcp', 'web'];

/** The single override variable. See the module comment for its grammar. */
export const SENTRY_SAMPLING_ENV_VAR = 'SENTRY_SAMPLING';

export interface ResolvedSentrySampling extends SentrySamplingPolicy {
  /** The environment the policy was read from, after alias folding. */
  environment: SentryDeployEnvironment;
  /** Kinds whose value came from `SENTRY_SAMPLING` rather than the table. */
  overridden: SentrySampleKind[];
  /** Override entries that were set but unusable, verbatim, for the boot log. */
  rejected: string[];
}

/**
 * Fold an environment string onto one of the four budgets.
 *
 * Unknown resolves to `prod` deliberately: a typo in `SENTRY_ENVIRONMENT`
 * should cost us visibility, which we notice, rather than money, which we
 * notice five weeks later on an invoice.
 */
export function normalizeEnvironment(raw: string | undefined): {
  environment: SentryDeployEnvironment;
  recognised: boolean;
} {
  const key = raw?.trim().toLowerCase() ?? '';
  const mapped = ENVIRONMENT_ALIASES[key];
  return mapped
    ? { environment: mapped, recognised: true }
    : { environment: 'prod', recognised: false };
}

/** Parse a 0..1 rate; `null` when absent, unparseable, or out of range. */
function parseRate(raw: string | undefined): number | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : null;
}

interface ParsedOverrides {
  /** `kind` -> rate, applying to every service. */
  wide: Partial<Record<SentrySampleKind, number>>;
  /** `kind` -> service -> rate. Beats `wide`. */
  scoped: Partial<Record<SentrySampleKind, Partial<Record<SentryService, number>>>>;
  rejected: string[];
}

/** Parse `traces.cron=1,profiles=0` into wide and service-scoped overrides. */
function parseOverrides(raw: string | undefined): ParsedOverrides {
  const result: ParsedOverrides = { wide: {}, scoped: {}, rejected: [] };
  const trimmed = raw?.trim();
  if (!trimmed) return result;

  for (const entry of trimmed.split(',')) {
    const text = entry.trim();
    if (!text) continue;

    const separator = text.indexOf('=');
    const target = separator === -1 ? '' : text.slice(0, separator).trim();
    const rate = separator === -1 ? null : parseRate(text.slice(separator + 1));
    if (!target || rate === null) {
      result.rejected.push(text);
      continue;
    }

    const [kindText, serviceText, ...extra] = target.split('.');
    // Kinds are matched case-insensitively so `TRACES.CRON` and `replaysOnError`
    // both work; nobody should have to remember our casing at 3am.
    const kind = KINDS.find((candidate) => candidate.toLowerCase() === kindText.toLowerCase());
    if (!kind || extra.length > 0) {
      result.rejected.push(text);
      continue;
    }

    if (serviceText === undefined) {
      result.wide[kind] = rate;
      continue;
    }

    const service = SERVICES.find((candidate) => candidate === serviceText.trim().toLowerCase());
    if (!service) {
      result.rejected.push(text);
      continue;
    }
    (result.scoped[kind] ??= {})[service] = rate;
  }

  return result;
}

/**
 * Resolve the full sampling policy for one service in one environment.
 *
 * `env` defaults to `process.env` where that exists and to an empty object in
 * the browser, so the same function serves the Node services and the Vite
 * bundle (which passes `import.meta.env`).
 */
export function resolveSentrySampling(input: {
  service: SentryService;
  environment: string | undefined;
  env?: Record<string, string | undefined>;
}): ResolvedSentrySampling {
  const env =
    input.env ??
    (typeof process === 'undefined' ? {} : (process.env as Record<string, string | undefined>));
  const { environment, recognised } = normalizeEnvironment(input.environment);
  const base = POLICY[environment][input.service];

  const overrides = parseOverrides(env[SENTRY_SAMPLING_ENV_VAR]);
  const rejected = [...overrides.rejected];
  if (!recognised && input.environment?.trim()) {
    rejected.push(
      `environment="${input.environment.trim()}" is not a known environment — using the prod budget`,
    );
  }

  const resolved: ResolvedSentrySampling = {
    ...base,
    environment,
    overridden: [],
    rejected,
  };

  for (const kind of KINDS) {
    const override = overrides.scoped[kind]?.[input.service] ?? overrides.wide[kind];
    if (override !== undefined) {
      resolved[kind] = override;
      resolved.overridden.push(kind);
    }
  }

  return resolved;
}

/**
 * One boot line describing the resolved policy.
 *
 * Always returns a line, even when nothing was overridden. Sampling that
 * silently differs from what somebody believes is configured is the failure
 * this module exists to end, and a deploy should be verifiable by a line that
 * IS in the log rather than by one that is not.
 */
export function describeSentrySampling(
  service: SentryService,
  resolved: ResolvedSentrySampling,
): string {
  const rates = KINDS.filter((kind) => service === 'web' || !kind.startsWith('replays'))
    .map((kind) => `${kind}=${resolved[kind]}${resolved.overridden.includes(kind) ? '*' : ''}`)
    .join(' ');
  const suffix = resolved.overridden.length ? ` (* from ${SENTRY_SAMPLING_ENV_VAR})` : '';
  const rejections = resolved.rejected.length
    ? ` IGNORED unusable ${SENTRY_SAMPLING_ENV_VAR} input: ${resolved.rejected.join('; ')}.`
    : '';
  return `[observability] ${service}@${resolved.environment} sampling: ${rates}${suffix}.${rejections}`;
}

/**
 * Root transactions to drop, as `Sentry.init({ ignoreTransactions })` patterns.
 *
 * These are matched against the *root* transaction name, so a Redis call or a
 * query that happens INSIDE a real transaction is a child span, keeps its
 * parent, and is unaffected. Only parentless noise matches.
 *
 * Measured over a week of our own ingest, the top groups were:
 *
 *     redis-evalsha             230K  \
 *     redis-bzpopmin            173K  /  65%  parentless Redis
 *     prisma:client:operation   127K  \
 *     prisma:client:transaction  69K  /  32%  parentless Prisma
 *     6x GET /api/v1/...        ~18K      3%  actual application traffic
 *
 * 97% of what we paid for was infrastructure operations with no parent.
 *
 * - `redis-*`: BullMQ's own traffic arrives as root transactions named after
 *   the command. `redis-bzpopmin` is the 5-second blocking pop that each of the
 *   44 queue workers sits in, permanently, and `redis-evalsha` is its Lua — a
 *   constant stream emitted whether or not the system is doing anything.
 * - `prisma:client:*`: the same disease with a different driver. A query
 *   reported as its own root transaction cannot say which job ran it, in what
 *   order, or how long that job took. It is a fact with no story.
 * - health and metrics endpoints: polled by the platform on a fixed interval,
 *   so they too are load-independent, and a 200 from a liveness probe has never
 *   diagnosed anything.
 *
 * These are orphans in the first place because nothing in the worker opened a
 * root span. `dispatchCronJob` now opens one per execution, named after the
 * job, so a cron's queries hang under a trace that describes the run. The 44
 * BullMQ processors still do not — each definition constructs its own `Worker`,
 * so there is no single place to wrap them — which is why the `worker` traces
 * rate buys very little until that is fixed, and why raising it would buy
 * nothing but orphans.
 */
export const IGNORED_ROOT_TRANSACTIONS: readonly RegExp[] = [
  /^redis-[a-z-]+$/i,
  // Named explicitly rather than `prisma:client:[a-z]+`, which also swallowed
  // `prisma:client:transaction` — the transaction Sentry's N+1 detector runs on. Dropping it
  // cost the detector its input: `ERP-WORKERS-6P` (an N+1 on `alchemy.budget`, once an hour
  // from `budget-utilization-reconcile`) had no other way to surface. An unlisted future
  // prisma span name is deliberately NOT dropped: noise we can see and add is cheaper than a
  // detector silently starved.
  /^prisma:client:(operation|compile)$/i,
  // The segment must END here: `\b` would also match `/health-insurance-plans`,
  // which is a real route shape in this repo. An over-broad ignore is worse than
  // no ignore — it drops the traces you needed and leaves no trace of doing so.
  /^(GET|HEAD)\s+\/(health|healthz|readyz|livez|metrics)(?:$|[/?])/i,
];

/**
 * Browser errors to drop before they are billed as events.
 *
 * Every entry is noise the application cannot act on: injected extension code,
 * a benign layout-loop warning browsers emit at scale, and the navigation and
 * connectivity aborts that a user closing a tab produces. None of them
 * represent a fault in our code, and all of them arrive in volume.
 */
export const IGNORED_BROWSER_ERRORS: readonly (string | RegExp)[] = [
  /^ResizeObserver loop/,
  /^Non-Error promise rejection captured/,
  'Network request failed',
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'AbortError',
  /extension:\//i,
  /^chrome-extension:/,
  /^moz-extension:/,
];
